import React, { useState, useEffect, useRef } from 'react';
import { useScreenRecorder } from './useScreenRecorder';
import { setUnsavedCapture } from '../shared/unsavedWork';
import { useOrientation } from '../shared';
import { ScreenOptions } from './ScreenOptions';
import { ScreenReady } from './ScreenReady';
import { ScreenRecording } from './ScreenRecording';
import { ScreenPreview } from './ScreenPreview';
import { ScreenReview } from './ScreenReview';
import { ScreenNotes } from './ScreenNotes';
import { GuidedCapture } from '../guided';
import type { InitialDraft } from '../shared';
import './ScreenCapture.css';

export type ScreenStep = 'options' | 'ready' | 'recording' | 'preview' | 'review' | 'notes';

const isMobileDevice = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ||
    (navigator.maxTouchPoints > 0 && /Macintosh/i.test(navigator.userAgent));
};

export interface ScreenCaptureProps {
  taskName: string;
  prompt: string;
  onSubmit: (videoBlob: Blob, notes: string) => void;
  onSaveDraft: (videoBlob: Blob | null, notes: string) => void;
  onClose: () => void;
  initialNotes?: string;
  initialDraft?: InitialDraft;
  /** Deep-link URL to launch the target app (mobile guided flow) */
  targetUrl?: string;
  /** Task ID for sessionStorage keying */
  taskId?: string;
}

export const ScreenCapture: React.FC<ScreenCaptureProps> = ({
  taskName,
  prompt,
  onSubmit,
  onSaveDraft,
  onClose,
  initialNotes,
  initialDraft,
  targetUrl,
  taskId,
}) => {
  // On mobile, delegate to GuidedCapture (native screen record + file upload)
  // matching the legacy ParticipantView behavior
  if (isMobileDevice()) {
    return (
      <GuidedCapture
        taskName={taskName}
        prompt={prompt}
        targetUrl={targetUrl}
        taskId={taskId}
        onSubmit={(file, notes) => onSubmit(file, notes)}
        onSaveDraft={(file, notes) => onSaveDraft(file, notes)}
        onClose={onClose}
        initialDraft={initialDraft}
      />
    );
  }

  // Desktop: getDisplayMedia flow
  const hasDraftMedia = !!initialDraft?.mediaBlobs.length;
  const [step, setStep] = useState<ScreenStep>(hasDraftMedia ? 'review' : 'options');
  const recorder = useScreenRecorder();
  const { isLandscape, orientation } = useOrientation();
  const injectedRef = useRef(false);

  /* Recording not yet saved → page-level leave nudge */
  useEffect(() => {
    setUnsavedCapture(!!recorder.videoBlob);
    return () => setUnsavedCapture(false);
  }, [recorder.videoBlob]);

  /* Inject draft video on mount */
  useEffect(() => {
    if (hasDraftMedia && !injectedRef.current) {
      injectedRef.current = true;
      recorder.injectVideo(
        initialDraft!.mediaBlobs[0],
        initialDraft!.mediaDurations?.[0] ?? 0,
        null,
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStartShare = async () => {
    try {
      await recorder.startShare();
      setStep('ready');
    } catch {
      // getDisplayMedia denied or failed
    }
  };

  const handleRecord = () => {
    recorder.startRecording(orientation);
    setStep('recording');
  };

  const handleStop = () => {
    recorder.stopRecording();
    setStep('preview');
  };

  const handleRetake = () => {
    recorder.reset();
    setStep('options');
  };

  const handleUseRecording = () => {
    recorder.stopShare();
    setStep('review');
  };

  const handleDeleteInReview = () => {
    recorder.reset();
    setStep('options');
  };

  const handleRecordAnother = () => {
    recorder.reset();
    setStep('options');
  };

  const handleSubmit = (notes: string) => {
    if (recorder.videoBlob) {
      onSubmit(recorder.videoBlob, notes);
    }
  };

  const handleSaveDraft = (notes: string) => {
    recorder.stopShare();
    onSaveDraft(recorder.videoBlob, notes);
  };

  const handleClose = () => {
    recorder.resetAll();
    onClose();
  };

  switch (step) {
    case 'options':
      return (
        <ScreenOptions
          taskName={taskName}
          mode={recorder.mode}
          enableMic={recorder.enableMic}
          enableSystemAudio={recorder.enableSystemAudio}
          onSetMode={recorder.setMode}
          onSetMic={recorder.setEnableMic}
          onSetSystemAudio={recorder.setEnableSystemAudio}
          onStartShare={handleStartShare}
          onClose={handleClose}
        />
      );

    case 'ready':
      return (
        <ScreenReady
          taskName={taskName}
          screenRef={recorder.screenRef}
          camRef={recorder.camRef}
          mode={recorder.mode}
          isLandscape={isLandscape}
          onRecord={handleRecord}
          onClose={handleClose}
        />
      );

    case 'recording':
      return (
        <ScreenRecording
          taskName={taskName}
          screenRef={recorder.screenRef}
          camRef={recorder.camRef}
          mode={recorder.mode}
          duration={recorder.duration}
          isLandscape={isLandscape}
          onStop={handleStop}
          onClose={handleClose}
        />
      );

    case 'preview':
      return (
        <ScreenPreview
          taskName={taskName}
          videoUrl={recorder.videoUrl}
          duration={recorder.duration}
          isLandscape={isLandscape}
          onRetake={handleRetake}
          onUseRecording={handleUseRecording}
          onClose={handleClose}
        />
      );

    case 'review':
      return (
        <ScreenReview
          taskName={taskName}
          videoUrl={recorder.videoUrl}
          duration={recorder.duration}
          mode={recorder.mode}
          onDelete={handleDeleteInReview}
          onRecordAnother={handleRecordAnother}
          onContinue={() => setStep('notes')}
          onClose={handleClose}
        />
      );

    case 'notes':
      return (
        <ScreenNotes
          taskName={taskName}
          videoUrl={recorder.videoUrl}
          duration={recorder.duration}
          mode={recorder.mode}
          onSubmit={handleSubmit}
          onSaveDraft={handleSaveDraft}
          onClose={handleClose}
          initialNotes={initialDraft?.notes || initialNotes}
        />
      );

    default:
      return null;
  }
};
